import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { listBlueprints, type BlueprintListResult } from '../services/blueprint.service';
import { useExcelenciaFiltersStore } from '../state/excelenciaFilters.store';

export function useBlueprintGridQuery() {
  const { organizationId, page, pageSize, sortDir, sortKey, status, category, search } = useExcelenciaFiltersStore();
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const queryKey = useMemo(
    () => ['excelencia-grid', organizationId, page, pageSize, sortKey, sortDir, status, category, debouncedSearch],
    [organizationId, page, pageSize, sortKey, sortDir, status, category, debouncedSearch],
  );

  const query = useQuery<BlueprintListResult, Error>({
    queryKey,
    enabled: Boolean(organizationId),
    queryFn: () => {
      if (!organizationId) return Promise.resolve({ data: [], total: 0, page, pageSize });
      return listBlueprints({ organizationId, page, pageSize, sortDir, sortKey, status, category, search: debouncedSearch });
    },
    keepPreviousData: true,
  });

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;
  const pagination = useMemo(() => ({ page, pageSize }), [page, pageSize]);

  return {
    data,
    total,
    pagination,
    hasData: data.length > 0,
    loading: query.isPending && !query.data,
    isFetching: query.isFetching,
    error: query.error?.message,
    refetch: query.refetch,
  };
}
